import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useInternals } from '../../context/internalContext';
import { useUsers } from '../../context/usersContext';
import FormattedInput from '../FormatdInput/formatFunctions';
import Skeleton from '../Skeleton';
import Lupa from '../../assets/icons/lupa.svg';
import './SearchInternal.css'

const SEARCH_DEBOUNCE_MS = 400;
const NAME_MIN_LENGTH = 3;
const CPF_LENGTH = 11;

const resolveSearchValue = (query, type) => {
  if (type === 'cpf') {
    const digits = query.replace(/\D/g, '');
    return digits.length === CPF_LENGTH ? digits : '';
  }
  const name = query.trim();
  return name.length >= NAME_MIN_LENGTH ? name : '';
};

const SearchInternal = ({ onSelect, onSelectingChange = null }) => {
  const location = useLocation();
  const { buildUnitReadParams } = useUsers();
  const { listInternals, listInternalImage } = useInternals();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [filteredInternals, setFilteredInternals] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const enrichInternalWithImages = useCallback(async (internal) => {
    if (!internal?.id) return internal;

    try {
      const imageResponse = await listInternalImage(internal.id);
      const rawImages = Array.isArray(imageResponse)
        ? imageResponse
        : Array.isArray(imageResponse?.data)
          ? imageResponse.data
          : [];

      const images = rawImages.filter((image) => {
        const imageInternalId = image?.internal?.id ?? image?.internal;
        if (imageInternalId === null || imageInternalId === undefined || imageInternalId === '') {
          return true;
        }
        return String(imageInternalId) === String(internal.id);
      });
      if (!images.length) return internal;

      return {
        ...internal,
        images,
      };
    } catch (error) {
      console.error('Erro ao buscar imagens do interno selecionado:', error);
      return internal;
    }
  }, [listInternalImage]);

  // Termo enviado à API (vazio = não busca): nome com 3+ letras; CPF só com os 11 dígitos.
  const searchValue = resolveSearchValue(searchQuery, searchType);

  // Busca com debounce: só consulta a API quando o usuário para de digitar, pra não estourar o
  // limite de requisições do backend. Resposta de um termo antigo que chegar atrasada é ignorada.
  // Para usuario sem unidade fixa, nao envia o filtro unit e busca de forma global.
  useEffect(() => {
    if (!searchValue) {
      setFilteredInternals([]);
      setIsSearching(false);
      return undefined;
    }

    let isCurrent = true;
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await listInternals(buildUnitReadParams({ [searchType]: searchValue }));
        if (isCurrent) setFilteredInternals(response?.results || []);
      } catch (error) {
        console.error('Erro ao buscar internos:', error);
        if (isCurrent) setFilteredInternals([]);
      } finally {
        if (isCurrent) setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [searchValue, searchType, listInternals, buildUnitReadParams]);

  // Troca Nome/CPF limpa o termo: o texto de um tipo não vale como filtro do outro.
  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchQuery('');
  };

  // Mantem Hanseniase e Tuberculose sem alteracao de comportamento visual por enquanto.
  const isLegacyVisualRoute = ['/hanseniase', '/tuberculose'].includes(location.pathname);
  const shouldShowDropdown = Boolean(searchValue) && (
    isLegacyVisualRoute
      ? filteredInternals.length > 0
      : (isSearching || filteredInternals.length > 0)
  );
  const shouldShowEmptyResult = !isLegacyVisualRoute
    && Boolean(searchValue)
    && !isSearching
    && filteredInternals.length === 0;

  const handleSelectInternal = async (internal) => {
    setIsSelecting(true);
    if (typeof onSelectingChange === 'function') {
      onSelectingChange(true);
    }
    try {
      const enrichedInternal = await enrichInternalWithImages(internal);
      await Promise.resolve(onSelect?.(enrichedInternal));
      setSearchQuery('');
      setFilteredInternals([]);
    } catch (error) {
      console.error('Erro ao selecionar interno:', error);
    } finally {
      setIsSelecting(false);
      if (typeof onSelectingChange === 'function') {
        onSelectingChange(false);
      }
    }
  };

  return (
    <div className="search-container">
      <form className="search-form" onSubmit={(e) => e.preventDefault()}>
        <div className="search-row">
          <select
            className="search-select"
            value={searchType}
            onChange={handleSearchTypeChange}
            disabled={isSelecting}
          >
            <option value="name">Nome</option>
            <option value="cpf">CPF</option>
          </select>

          <div className="search-wrapper">
            <FormattedInput
              type={searchType === 'name' ? 'nome' : 'cpf'}
              placeholder={`Buscar por ${searchType === 'name' ? 'Nome' : 'CPF'}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isSelecting}
            />
            <img src={Lupa} alt="Lupa" className="search-icon" />

            {shouldShowDropdown && (
              <div className="social-search-results">
                {isSearching ? (
                  <div className="social-search-result-item search-result-loading">
                    <Skeleton
                      type="stack"
                      lines={1}
                      text="Buscando internos..."
                      prefix="search-internals-loading"
                    />
                  </div>
                ) : (
                  filteredInternals.map((internal) => (
                    <div
                      key={internal.id}
                      onClick={() => handleSelectInternal(internal)}
                      className="social-search-result-item"
                    >
                      {internal.name} - {internal.cpf}
                    </div>
                  ))
                )}
              </div>
            )}

            {shouldShowEmptyResult && (
              <div className="social-search-results">
                <div className="social-search-result-item search-result-empty">
                  Nenhum interno encontrado.
                </div>
              </div>
            )}
          </div>
        </div>

      </form>
    </div>
  );
};

export default SearchInternal;
